// src/api-errors.ts
var ApiError = {
  // Generic
  VALIDATION_FAILED: "VALIDATION_FAILED",
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  INTERNAL_ERROR: "INTERNAL_ERROR",
  NOT_FOUND: "NOT_FOUND",
  RATE_LIMITED: "RATE_LIMITED",
  // Tokens (invite, reset)
  TOKEN_INVALID: "TOKEN_INVALID",
  TOKEN_EXPIRED: "TOKEN_EXPIRED",
  // User / account
  EMAIL_ALREADY_EXISTS: "EMAIL_ALREADY_EXISTS",
  USER_ALREADY_EXISTS: "USER_ALREADY_EXISTS",
  USER_NOT_FOUND: "USER_NOT_FOUND",
  ACCOUNT_CREATION_FAILED: "ACCOUNT_CREATION_FAILED",
  STAFF_CANNOT_CHANGE_EMAIL: "STAFF_CANNOT_CHANGE_EMAIL",
  CANNOT_MANAGE_SELF: "CANNOT_MANAGE_SELF",
  TARGET_NOT_STAFF: "TARGET_NOT_STAFF",
  PASSWORD_RESET_NOT_INVITED: "PASSWORD_RESET_NOT_INVITED",
  CURRENT_PASSWORD_REQUIRED: "CURRENT_PASSWORD_REQUIRED",
  PASSWORD_INCORRECT: "PASSWORD_INCORRECT",
  NEW_PASSWORD_SAME: "NEW_PASSWORD_SAME",
  NO_CHANGES: "NO_CHANGES",
  NO_PASSWORD_SET: "NO_PASSWORD_SET",
  // Player @handle (public username for opponent search + sharing)
  HANDLE_INVALID: "HANDLE_INVALID",
  HANDLE_TAKEN: "HANDLE_TAKEN",
  // Invite flow
  STAFF_CANNOT_INVITE: "STAFF_CANNOT_INVITE",
  CLUB_REQUIRED: "CLUB_REQUIRED",
  CLUB_SLUG_EXISTS: "CLUB_SLUG_EXISTS",
  MANAGER_NO_CLUB: "MANAGER_NO_CLUB",
  INVITE_FAILED: "INVITE_FAILED",
  // Field validation (used as Zod messages — clients read parsed.error.issues[i].message)
  EMAIL_INVALID: "EMAIL_INVALID",
  EMAIL_REQUIRED: "EMAIL_REQUIRED",
  EMAIL_NOT_VERIFIED: "EMAIL_NOT_VERIFIED",
  LOGIN_REQUIRED: "LOGIN_REQUIRED",
  PHONE_REQUIRED: "PHONE_REQUIRED",
  AT_LEAST_ONE_CONTACT_REQUIRED: "AT_LEAST_ONE_CONTACT_REQUIRED",
  EMAIL_OTP_REQUIRED: "EMAIL_OTP_REQUIRED",
  PASSWORD_TOO_SHORT: "PASSWORD_TOO_SHORT",
  PASSWORD_MISSING_UPPER: "PASSWORD_MISSING_UPPER",
  PASSWORD_MISSING_NUMBER: "PASSWORD_MISSING_NUMBER",
  FIRSTNAME_TOO_SHORT: "FIRSTNAME_TOO_SHORT",
  LASTNAME_TOO_SHORT: "LASTNAME_TOO_SHORT",
  PHONE_INVALID: "PHONE_INVALID",
  CLUBNAME_TOO_SHORT: "CLUBNAME_TOO_SHORT",
  CLUBSLUG_TOO_SHORT: "CLUBSLUG_TOO_SHORT",
  CLUBSLUG_INVALID: "CLUBSLUG_INVALID",
  PASSWORDS_DO_NOT_MATCH: "PASSWORDS_DO_NOT_MATCH",
  TOKEN_REQUIRED: "TOKEN_REQUIRED",
  // Onboarding / club CRUD
  ADDRESS_REQUIRED: "ADDRESS_REQUIRED",
  CITY_REQUIRED: "CITY_REQUIRED",
  COUNTRY_REQUIRED: "COUNTRY_REQUIRED",
  MAPS_URL_INVALID: "MAPS_URL_INVALID",
  WEBSITE_INVALID: "WEBSITE_INVALID",
  WORKING_HOURS_INVALID: "WORKING_HOURS_INVALID",
  COURT_HOURS_OUTSIDE_CLUB: "COURT_HOURS_OUTSIDE_CLUB",
  BOOKING_DAYS_INVALID: "BOOKING_DAYS_INVALID",
  BOOKING_LIMIT_INVALID: "BOOKING_LIMIT_INVALID",
  SPORTS_REQUIRED: "SPORTS_REQUIRED",
  SPORT_INVALID: "SPORT_INVALID",
  AMENITY_INVALID: "AMENITY_INVALID",
  SESSION_DURATION_INVALID: "SESSION_DURATION_INVALID",
  COURTS_REQUIRED: "COURTS_REQUIRED",
  COURT_NAME_REQUIRED: "COURT_NAME_REQUIRED",
  COURT_PRICE_INVALID: "COURT_PRICE_INVALID",
  COURT_SPORT_MISMATCH: "COURT_SPORT_MISMATCH",
  COURT_DUPLICATE_NAME: "COURT_DUPLICATE_NAME",
  COURT_SURFACE_INVALID: "COURT_SURFACE_INVALID",
  COURT_SIZE_INVALID: "COURT_SIZE_INVALID",
  COURT_HAS_FUTURE_BOOKINGS: "COURT_HAS_FUTURE_BOOKINGS",
  // Splittable basketball courts (whole court + two independently-bookable
  // halves). Split config is only valid for BASKETBALL; the half price is
  // required when enabling; disabling is blocked while a half has future
  // bookings (admin UI preflights via GET /edit-impact?kind=court-unsplit).
  COURT_SPLIT_UNSUPPORTED_SPORT: "COURT_SPLIT_UNSUPPORTED_SPORT",
  COURT_HALF_PRICE_REQUIRED: "COURT_HALF_PRICE_REQUIRED",
  COURT_SPLIT_HAS_FUTURE_BOOKINGS: "COURT_SPLIT_HAS_FUTURE_BOOKINGS",
  // Server-side guards for admin edits that would regrid the slot timeline
  // and create real overlaps with existing bookings. Returned 409. The admin
  // UI also preflights via GET /edit-impact so it can show the count up-front
  // instead of relying on the error response.
  SPORT_DURATION_HAS_FUTURE_BOOKINGS: "SPORT_DURATION_HAS_FUTURE_BOOKINGS",
  COURT_SPORT_CHANGE_HAS_FUTURE_BOOKINGS: "COURT_SPORT_CHANGE_HAS_FUTURE_BOOKINGS",
  COURT_NOT_FOUND: "COURT_NOT_FOUND",
  MAX_COURTS_REACHED: "MAX_COURTS_REACHED",
  MAX_COURTS_INVALID: "MAX_COURTS_INVALID",
  SPORT_HAS_COURTS: "SPORT_HAS_COURTS",
  SPORT_DUPLICATE: "SPORT_DUPLICATE",
  SPORT_NOT_FOUND: "SPORT_NOT_FOUND",
  ALREADY_ONBOARDED: "ALREADY_ONBOARDED",
  ONBOARDING_INCOMPLETE: "ONBOARDING_INCOMPLETE",
  CLUB_NOT_FOUND: "CLUB_NOT_FOUND",
  DELETE_CONFIRM_MISMATCH: "DELETE_CONFIRM_MISMATCH",
  // Paid subscription expired (or never set) — write endpoints reject and
  // the public booking page renders an overlay over the picker.
  SUBSCRIPTION_EXPIRED: "SUBSCRIPTION_EXPIRED",
  // Pro/Enterprise feature toggled on while the club is on a lower tier.
  // Returned 402 from PUT /features and /limits. Admin UI surfaces a
  // "Pro" badge + tooltip on the gated inputs to prevent this client-side.
  FEATURE_REQUIRES_UPGRADE: "FEATURE_REQUIRES_UPGRADE",
  // Booking
  BOOKING_IN_PAST: "BOOKING_IN_PAST",
  RECURRENCE_INVALID: "RECURRENCE_INVALID",
  SERIES_NOT_FOUND: "SERIES_NOT_FOUND",
  BOOKING_NOT_FOUND: "BOOKING_NOT_FOUND",
  // Reschedule
  RESCHEDULE_WINDOW_PASSED: "RESCHEDULE_WINDOW_PASSED",
  RESCHEDULE_ALREADY_USED: "RESCHEDULE_ALREADY_USED",
  RESCHEDULE_SAME_SLOT: "RESCHEDULE_SAME_SLOT",
  RESCHEDULE_DATE_OUT_OF_RANGE: "RESCHEDULE_DATE_OUT_OF_RANGE",
  RESCHEDULE_SPORT_MISMATCH: "RESCHEDULE_SPORT_MISMATCH",
  RESCHEDULE_SLOT_OUTSIDE_HOURS: "RESCHEDULE_SLOT_OUTSIDE_HOURS",
  SLOT_TAKEN: "SLOT_TAKEN",
  // Block slots
  BLOCK_REASON_REQUIRED: "BLOCK_REASON_REQUIRED",
  BLOCK_TIME_RANGE_INVALID: "BLOCK_TIME_RANGE_INVALID",
  BLOCK_NO_SLOTS: "BLOCK_NO_SLOTS",
  BLOCK_NOT_FOUND: "BLOCK_NOT_FOUND",
  // Closures
  CLOSURE_NOT_FOUND: "CLOSURE_NOT_FOUND",
  CLUB_CLOSED: "CLUB_CLOSED",
  // Club membership
  MEMBERSHIP_REQUIRED: "MEMBERSHIP_REQUIRED",
  MEMBERSHIP_COOLDOWN: "MEMBERSHIP_COOLDOWN",
  MEMBERSHIP_BANNED: "MEMBERSHIP_BANNED",
  MEMBERSHIP_NOT_FOUND: "MEMBERSHIP_NOT_FOUND",
  MEMBERSHIP_ALREADY_APPROVED: "MEMBERSHIP_ALREADY_APPROVED",
  MEMBERSHIP_ALREADY_PENDING: "MEMBERSHIP_ALREADY_PENDING",
  MEMBERSHIP_NOT_APPROVED: "MEMBERSHIP_NOT_APPROVED",
  MEMBERSHIP_NOT_REJECTED: "MEMBERSHIP_NOT_REJECTED",
  // Member-only slot rules
  MEMBER_SLOT_RESTRICTED: "MEMBER_SLOT_RESTRICTED",
  MEMBER_SLOTS_FLAG_OFF: "MEMBER_SLOTS_FLAG_OFF",
  // Time-slot management
  MAINTENANCE_SLOT: "MAINTENANCE_SLOT",
  TIME_SLOT_INVALID: "TIME_SLOT_INVALID",
  SLOT_PERIODS_INVALID: "SLOT_PERIODS_INVALID",
  SLOT_PERIODS_OVERLAP: "SLOT_PERIODS_OVERLAP",
  // Per-court pricing schedule (day-of-week + time window → price).
  PRICE_RULE_INVALID: "PRICE_RULE_INVALID",
  // Per-member booking quotas (clubs with membership + enforceBookingQuotas).
  // Late-booking exemption window bypasses these without counting.
  DAILY_LIMIT_EXCEEDED: "DAILY_LIMIT_EXCEEDED",
  CONSECUTIVE_DAY_NOT_ALLOWED: "CONSECUTIVE_DAY_NOT_ALLOWED",
  PRIME_TIME_QUOTA_EXCEEDED: "PRIME_TIME_QUOTA_EXCEEDED",
  // Per-user cap on bookings created within one calendar day (any target
  // dates). Keyed to the booking's active identity (account/phone/email).
  // Applies to every club, independent of the per-member quotas above.
  CREATION_DAY_LIMIT_EXCEEDED: "CREATION_DAY_LIMIT_EXCEEDED",
  // Roster (per-sport additional player names)
  ROSTER_REQUIRED: "ROSTER_REQUIRED",
  ROSTER_PLAYER_NAME_REQUIRED: "ROSTER_PLAYER_NAME_REQUIRED",
  // Coaching (coached sessions; PRO+ via Club.allowCoaching)
  COACHING_DISABLED: "COACHING_DISABLED",
  COACH_NOT_FOUND: "COACH_NOT_FOUND",
  COACH_INACTIVE: "COACH_INACTIVE",
  COACH_SPORT_INVALID: "COACH_SPORT_INVALID",
  COACH_UNAVAILABLE: "COACH_UNAVAILABLE",
  COACH_TAKEN: "COACH_TAKEN",
  COACH_PRICE_INVALID: "COACH_PRICE_INVALID",
  COACH_AVAILABILITY_INVALID: "COACH_AVAILABILITY_INVALID",
  COACH_TIME_OFF_INVALID: "COACH_TIME_OFF_INVALID",
  // Gear rental (rentable equipment; Club.allowGearRental, all tiers)
  GEAR_DISABLED: "GEAR_DISABLED",
  GEAR_NOT_FOUND: "GEAR_NOT_FOUND",
  GEAR_SPORT_INVALID: "GEAR_SPORT_INVALID",
  GEAR_UNAVAILABLE: "GEAR_UNAVAILABLE",
  GEAR_NAME_REQUIRED: "GEAR_NAME_REQUIRED",
  GEAR_PRICE_INVALID: "GEAR_PRICE_INVALID",
  GEAR_QUANTITY_INVALID: "GEAR_QUANTITY_INVALID",
  // Match results / fixtures (player-recorded match outcomes; opponent confirm)
  // The club hasn't enabled the results feature (Club.resultsEnabled).
  RESULTS_DISABLED: "RESULTS_DISABLED",
  MATCH_NOT_FOUND: "MATCH_NOT_FOUND",
  // A result already exists for this booking (one match per booking).
  MATCH_ALREADY_RECORDED: "MATCH_ALREADY_RECORDED",
  // The session hasn't ended yet — results can only be recorded afterwards.
  MATCH_SESSION_NOT_ENDED: "MATCH_SESSION_NOT_ENDED",
  // Caller didn't take part in the booking, so can't record/confirm its result.
  MATCH_NOT_PARTICIPANT: "MATCH_NOT_PARTICIPANT",
  // Booking is cancelled / no-show / unbookable — not eligible for a result.
  MATCH_BOOKING_NOT_ELIGIBLE: "MATCH_BOOKING_NOT_ELIGIBLE",
  // Only a registered opponent (other side) may confirm/dispute, and only while
  // the match is still PENDING confirmation.
  MATCH_CONFIRM_FORBIDDEN: "MATCH_CONFIRM_FORBIDDEN",
  MATCH_NOT_PENDING: "MATCH_NOT_PENDING",
  // Zod field messages
  MATCH_PLAYER_NAME_REQUIRED: "MATCH_PLAYER_NAME_REQUIRED",
  MATCH_RESULT_INVALID: "MATCH_RESULT_INVALID",
  // Competitions / leagues (Club.competitionsEnabled; PRO+)
  COMPETITIONS_DISABLED: "COMPETITIONS_DISABLED",
  COMPETITION_NOT_FOUND: "COMPETITION_NOT_FOUND",
  // Registration isn't open (status != REGISTRATION_OPEN).
  COMPETITION_NOT_OPEN: "COMPETITION_NOT_OPEN",
  COMPETITION_REGISTRATION_CLOSED: "COMPETITION_REGISTRATION_CLOSED",
  COMPETITION_FULL: "COMPETITION_FULL",
  COMPETITION_ALREADY_REGISTERED: "COMPETITION_ALREADY_REGISTERED",
  COMPETITION_NOT_REGISTERED: "COMPETITION_NOT_REGISTERED",
  // A fixture (competition Match) wasn't found or the caller isn't on it.
  COMPETITION_FIXTURE_NOT_FOUND: "COMPETITION_FIXTURE_NOT_FOUND",
  COMPETITION_NOT_PARTICIPANT: "COMPETITION_NOT_PARTICIPANT",
  // The requested action isn't valid for the competition's current status, or
  // the structure/result payload is inconsistent.
  COMPETITION_INVALID_STATE: "COMPETITION_INVALID_STATE",
  COMPETITION_RESULT_INVALID: "COMPETITION_RESULT_INVALID",
  COMPETITION_PARTICIPANT_NOT_FOUND: "COMPETITION_PARTICIPANT_NOT_FOUND",
  // Zod field messages
  COMPETITION_NAME_REQUIRED: "COMPETITION_NAME_REQUIRED",
  COMPETITION_FORMAT_INVALID: "COMPETITION_FORMAT_INVALID"
};

// src/primitives.ts
import { z } from "zod";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
var HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
var ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
var SPORT_KEYS = [
  "TENNIS",
  "PADEL",
  "BASKETBALL",
  "FOOTBALL",
  "VOLLEYBALL",
  "SQUASH",
  "TABLE_TENNIS",
  "BADMINTON",
  "HANDBALL",
  "PICKLEBALL"
];
var SURFACE_KEYS = [
  "CLAY",
  "HARD",
  "CONCRETE",
  "ASPHALT",
  "GRASS",
  "NATURAL_GRASS",
  "ARTIFICIAL_GRASS",
  "CARPET",
  "PARQUET",
  "RUBBER",
  "SAND",
  "SYNTHETIC",
  "MAT"
];
var SPORT_SURFACES = {
  TENNIS: ["CLAY", "HARD", "CONCRETE", "GRASS", "CARPET", "ARTIFICIAL_GRASS"],
  PADEL: [],
  BASKETBALL: ["PARQUET", "CONCRETE", "ASPHALT", "RUBBER"],
  FOOTBALL: ["NATURAL_GRASS", "ARTIFICIAL_GRASS", "CONCRETE", "PARQUET"],
  VOLLEYBALL: ["SAND", "PARQUET", "SYNTHETIC", "CONCRETE"],
  SQUASH: [],
  TABLE_TENNIS: [],
  BADMINTON: ["PARQUET", "SYNTHETIC", "MAT"],
  HANDBALL: ["PARQUET", "SYNTHETIC", "CONCRETE"],
  PICKLEBALL: ["HARD", "CONCRETE", "CARPET"]
};
function isSurfaceValidForSport(sport, surface) {
  if (surface == null || surface === "") return true;
  const allowed = SPORT_SURFACES[sport];
  if (!allowed) return false;
  return allowed.includes(surface);
}
var SIZE_KEYS = [
  "FIVE_A_SIDE",
  "SIX_A_SIDE",
  "SEVEN_A_SIDE",
  "EIGHT_A_SIDE",
  "ELEVEN_A_SIDE",
  "FUTSAL"
];
var SPORT_SIZES = {
  TENNIS: [],
  PADEL: [],
  BASKETBALL: [],
  FOOTBALL: [
    "FIVE_A_SIDE",
    "SIX_A_SIDE",
    "SEVEN_A_SIDE",
    "EIGHT_A_SIDE",
    "ELEVEN_A_SIDE",
    "FUTSAL"
  ],
  VOLLEYBALL: [],
  SQUASH: [],
  TABLE_TENNIS: [],
  BADMINTON: [],
  HANDBALL: [],
  PICKLEBALL: []
};
function isSizeValidForSport(sport, size) {
  if (size == null || size === "") return true;
  const allowed = SPORT_SIZES[sport];
  if (!allowed) return false;
  return allowed.includes(size);
}
function normalizePhone(input) {
  try {
    if (!isValidPhoneNumber(input, "RS")) return null;
    return parsePhoneNumber(input, "RS").number;
  } catch {
    return null;
  }
}
var idParam = z.string().trim().min(1).max(128);
var slugParam = z.string().trim().min(1).max(120).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
var tokenParam = z.string().trim().min(8).max(256);
var hhmmString = z.string().regex(HHMM);
var dateString = z.string().regex(ISO_DATE);
var sportEnum = z.enum(SPORT_KEYS);
var surfaceEnum = z.enum(SURFACE_KEYS);
var sizeEnum = z.enum(SIZE_KEYS);
var paginationQuery = z.object({
  page: z.coerce.number().int().min(1).catch(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).catch(20).default(20)
});
var clubSlugParams = z.object({ slug: slugParam });
var clubIdParams = z.object({ clubId: idParam });
var tokenParams = z.object({ token: tokenParam });
var dateRangeQuery = z.object({
  startDate: z.string().regex(ISO_DATE).optional().catch(void 0),
  endDate: z.string().regex(ISO_DATE).optional().catch(void 0)
});
var fromDateQuery = z.object({
  fromDate: z.string().regex(ISO_DATE).optional().catch(void 0)
});

// src/password.ts
import { z as z2 } from "zod";
var passwordSchema = z2.string().min(8, ApiError.PASSWORD_TOO_SHORT).regex(/[A-Z]/, ApiError.PASSWORD_MISSING_UPPER).regex(/[0-9]/, ApiError.PASSWORD_MISSING_NUMBER);

// src/permissions.ts
import { z as z3 } from "zod";
var staffPermissionsSchema = z3.object({
  view_bookings: z3.boolean(),
  create_booking: z3.boolean(),
  cancel_booking: z3.boolean(),
  edit_booking: z3.boolean(),
  manage_blocks: z3.boolean(),
  manage_closures: z3.boolean(),
  view_revenue: z3.boolean(),
  override_booking_limits: z3.boolean()
}).partial();
var STAFF_PERMISSION_DEFAULTS = {
  view_bookings: true,
  create_booking: true,
  cancel_booking: true,
  edit_booking: false,
  manage_blocks: true,
  manage_closures: false,
  view_revenue: false,
  override_booking_limits: false
};
function mergeStaffPermissions(override) {
  if (!override) return { ...STAFF_PERMISSION_DEFAULTS };
  const out = { ...STAFF_PERMISSION_DEFAULTS };
  for (const key of Object.keys(STAFF_PERMISSION_DEFAULTS)) {
    if (typeof override[key] === "boolean") out[key] = override[key];
  }
  return out;
}
function hasPermission(user, perm) {
  if (user.role === "SUPERADMIN" || user.role === "MANAGER") return true;
  if (user.role !== "STAFF") return false;
  const perms = user.permissions;
  return Boolean(perms?.[perm]);
}

// src/auth.ts
import { z as z4 } from "zod";
var registerSchema = z4.object({
  email: z4.string().email(ApiError.EMAIL_INVALID),
  password: passwordSchema,
  firstName: z4.string().min(2, ApiError.FIRSTNAME_TOO_SHORT),
  lastName: z4.string().min(2, ApiError.LASTNAME_TOO_SHORT),
  phone: z4.string().min(7, ApiError.PHONE_INVALID),
  role: z4.enum(["SUPERADMIN", "MANAGER", "STAFF"]).default("STAFF")
});
var inviteSchema = z4.object({
  email: z4.string().email(ApiError.EMAIL_INVALID),
  // Optional: SUPERADMIN may explicitly choose what role they're inviting.
  // MANAGER inviters invite STAFF or COACH; the role distinguishes which.
  role: z4.enum(["MANAGER", "STAFF", "COACH"]).optional(),
  clubId: z4.string().optional(),
  clubName: z4.string().min(2, ApiError.CLUBNAME_TOO_SHORT).optional(),
  clubSlug: z4.string().min(2, ApiError.CLUBSLUG_TOO_SHORT).regex(/^[a-z0-9-]+$/, ApiError.CLUBSLUG_INVALID).optional(),
  // STAFF-only override map; merged over STAFF_PERMISSION_DEFAULTS server-side.
  // Ignored on MANAGER invites.
  permissions: staffPermissionsSchema.optional(),
  // SUPERADMIN-only court cap. Applied to the freshly-created club on
  // MANAGER invites. Null/omitted = unlimited.
  maxCourts: z4.number().int(ApiError.MAX_COURTS_INVALID).min(1, ApiError.MAX_COURTS_INVALID).max(1e3, ApiError.MAX_COURTS_INVALID).nullable().optional()
});
var createAccountSchema = z4.object({
  token: z4.string().min(1, ApiError.TOKEN_REQUIRED),
  firstName: z4.string().min(2, ApiError.FIRSTNAME_TOO_SHORT),
  lastName: z4.string().min(2, ApiError.LASTNAME_TOO_SHORT),
  password: passwordSchema,
  confirmPassword: z4.string()
}).refine((d) => d.password === d.confirmPassword, {
  message: ApiError.PASSWORDS_DO_NOT_MATCH,
  path: ["confirmPassword"]
});
var loginSchema = z4.object({
  email: z4.string().trim().email(ApiError.EMAIL_INVALID),
  password: z4.string().min(1, ApiError.LOGIN_REQUIRED)
});
var forgotPasswordSchema = z4.object({
  email: z4.string().trim().email(ApiError.EMAIL_INVALID)
});
var resetPasswordSchema = z4.object({
  password: passwordSchema,
  confirmPassword: z4.string()
}).refine((d) => d.password === d.confirmPassword, {
  message: ApiError.PASSWORDS_DO_NOT_MATCH,
  path: ["confirmPassword"]
});

// src/contact.ts
import { z as z5 } from "zod";
var contactSchema = z5.object({
  name: z5.string().trim().min(2).max(120),
  email: z5.string().trim().email().max(200),
  phone: z5.string().trim().max(40).optional().or(z5.literal("")),
  clubName: z5.string().trim().max(160).optional().or(z5.literal("")),
  message: z5.string().trim().min(5).max(4e3),
  locale: z5.string().trim().max(8).optional(),
  topic: z5.enum(["club", "help"]).optional().default("club")
});

// src/verification.ts
import { z as z6 } from "zod";
var otpLanguage = z6.enum(["sr", "en", "ru"]);
var requestPhoneVerificationSchema = z6.object({
  phone: z6.string().trim().min(1),
  language: otpLanguage.optional()
});
var confirmPhoneVerificationSchema = z6.object({
  phone: z6.string().trim().min(1),
  code: z6.string().trim().min(1)
});
var requestEmailVerificationSchema = z6.object({
  email: z6.string().trim().min(1),
  language: otpLanguage.optional()
});
var confirmEmailVerificationSchema = z6.object({
  email: z6.string().trim().min(1),
  code: z6.string().trim().min(1)
});

// src/player.ts
import { z as z7 } from "zod";
var HANDLE_RE = /^[a-z][a-z0-9_.]{2,29}$/;
var handleSchema = z7.string().trim().toLowerCase().regex(HANDLE_RE, ApiError.HANDLE_INVALID);
var playerProfileSchema = z7.object({
  name: z7.string().trim().min(2, ApiError.FIRSTNAME_TOO_SHORT).max(100, ApiError.FIRSTNAME_TOO_SHORT),
  // Phone is required on edit just like on signup; the booking flow assumes
  // every PlayerUser has a reachable phone on file.
  phone: z7.string().trim().min(1, ApiError.PHONE_INVALID),
  // Optional on edit. Omitted = leave the existing handle untouched (the server
  // auto-generates one from the name on first profile completion if still null).
  handle: handleSchema.optional()
});

// src/time-slots.ts
import { z as z9 } from "zod";

// src/price-rules.ts
import { z as z8 } from "zod";
var priceRuleSchema = z8.object({
  days: z8.array(z8.number().int().min(0).max(6)).min(1, ApiError.PRICE_RULE_INVALID).max(7, ApiError.PRICE_RULE_INVALID),
  startTime: z8.string().regex(HHMM, ApiError.WORKING_HOURS_INVALID),
  endTime: z8.string().regex(HHMM, ApiError.WORKING_HOURS_INVALID),
  // Accept number or decimal string (mirrors TimeSlot.priceOverride). The
  // handler normalizes to a Prisma.Decimal before persisting.
  price: z8.union([
    z8.number().min(0).max(1e6),
    z8.string().regex(/^\d+(\.\d{1,2})?$/)
  ])
}).refine((r) => r.startTime < r.endTime, {
  message: ApiError.PRICE_RULE_INVALID,
  path: ["endTime"]
});
var courtPriceRulesSchema = z8.array(priceRuleSchema).nullable();
function dayOfWeekOf(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}
function resolveSlotPrice(input) {
  const { basePrice, priceOverride, rules, dayOfWeek, startTime } = input;
  if (priceOverride !== null && priceOverride !== void 0 && priceOverride !== "") {
    return String(priceOverride);
  }
  if (rules) {
    for (const rule of rules) {
      if (!rule.days.includes(dayOfWeek)) continue;
      if (startTime >= rule.startTime && startTime < rule.endTime) {
        return String(rule.price);
      }
    }
  }
  return String(basePrice);
}
function addPrice(a, b) {
  const total = Number(a) + Number(b);
  return (Math.round(total * 100) / 100).toString();
}
function priceRulesOverlap(rules) {
  for (let i = 0; i < rules.length; i++) {
    for (let j = i + 1; j < rules.length; j++) {
      const a = rules[i];
      const b = rules[j];
      if (!a.days.some((d) => b.days.includes(d))) continue;
      if (a.startTime < b.endTime && b.startTime < a.endTime) return true;
    }
  }
  return false;
}

// src/time-slots.ts
var slotPeriodSchema = z9.object({
  startTime: z9.string().regex(HHMM, ApiError.WORKING_HOURS_INVALID),
  durationMinutes: z9.number().int(ApiError.SLOT_PERIODS_INVALID).min(15, ApiError.SLOT_PERIODS_INVALID).max(480, ApiError.SLOT_PERIODS_INVALID)
});
var timeSlotRuleSchema = z9.object({
  startTime: z9.string().regex(HHMM, ApiError.WORKING_HOURS_INVALID),
  memberOnly: z9.boolean().optional().default(false),
  isPrime: z9.boolean().optional().default(false),
  isMaintenance: z9.boolean().optional().default(false),
  // Accept number, decimal string, or null. The handler normalizes to Decimal.
  priceOverride: z9.union([
    z9.number().min(0).max(1e6),
    z9.string().regex(/^\d+(\.\d{1,2})?$/),
    z9.null()
  ]).optional()
});
var courtTimeSlotsUpdateSchema = z9.object({
  periods: z9.array(slotPeriodSchema).nullable().optional(),
  rules: z9.array(timeSlotRuleSchema),
  priceRules: courtPriceRulesSchema.optional()
});
var timeSlotsUpdateSchema = z9.object({
  courts: z9.record(z9.string().min(1), courtTimeSlotsUpdateSchema),
  allowMemberOnlySlots: z9.boolean().optional(),
  allowPrimeTimeSlots: z9.boolean().optional()
});

// src/recurrence.ts
import { z as z10 } from "zod";
var weekdaySchema = z10.union([
  z10.literal(1),
  z10.literal(2),
  z10.literal(3),
  z10.literal(4),
  z10.literal(5),
  z10.literal(6),
  z10.literal(7)
]);
var recurrenceSchema = z10.discriminatedUnion("frequency", [
  z10.object({
    frequency: z10.literal("DAILY"),
    startDate: z10.string().regex(ISO_DATE),
    untilDate: z10.string().regex(ISO_DATE)
  }),
  z10.object({
    frequency: z10.literal("WEEKLY"),
    startDate: z10.string().regex(ISO_DATE),
    untilDate: z10.string().regex(ISO_DATE),
    weekdays: z10.array(weekdaySchema).min(1).max(7)
  })
]);

// src/blocks.ts
import { z as z11 } from "zod";
var blockBulkSchema = z11.object({
  courtIds: z11.array(z11.string().min(1)).min(1),
  sportId: z11.string().min(1),
  // Each range maps to N session slots; rows are stored per slot start.
  timeRanges: z11.array(
    z11.object({
      start: z11.string().regex(HHMM),
      end: z11.string().regex(HHMM)
    })
  ).min(1).max(8),
  reason: z11.string().trim().min(1).max(200),
  // Either a one-off date or a recurrence — exactly one.
  date: z11.string().regex(ISO_DATE).optional(),
  recurrence: recurrenceSchema.optional()
}).refine((v) => Boolean(v.date) !== Boolean(v.recurrence), {
  message: "Provide exactly one of date or recurrence",
  path: ["date"]
});
var blocksListQuery = dateRangeQuery.extend({
  seriesId: z11.string().optional().catch(void 0)
});
var blockIdParams = z11.object({ clubId: idParam, id: idParam });
var blockSeriesParams = z11.object({
  clubId: idParam,
  seriesId: idParam
});

// src/closures.ts
import { z as z12 } from "zod";
var closureBulkSchema = z12.object({
  reason: z12.string().trim().min(1).max(200),
  date: z12.string().regex(ISO_DATE).optional(),
  recurrence: recurrenceSchema.optional()
}).refine((v) => Boolean(v.date) !== Boolean(v.recurrence), {
  message: "Provide exactly one of date or recurrence",
  path: ["date"]
});
var closureIdParams = z12.object({ clubId: idParam, id: idParam });
var closureSeriesParams = z12.object({
  clubId: idParam,
  seriesId: idParam
});

// src/onboarding.ts
import { z as z13 } from "zod";
var updateMeSchema = z13.object({
  firstName: z13.string().trim().min(2, ApiError.FIRSTNAME_TOO_SHORT).optional(),
  lastName: z13.string().trim().min(2, ApiError.LASTNAME_TOO_SHORT).optional(),
  phone: z13.string().trim().optional(),
  preferredLanguage: z13.enum(["sr", "en", "ru"]).optional(),
  email: z13.string().trim().email(ApiError.EMAIL_INVALID).optional(),
  currentPassword: z13.string().optional()
}).strict();
var changePasswordSchema = z13.object({
  currentPassword: z13.string().min(1, ApiError.CURRENT_PASSWORD_REQUIRED),
  newPassword: passwordSchema
});
var clubInfoSchema = z13.object({
  address: z13.string().trim().min(3, ApiError.ADDRESS_REQUIRED),
  city: z13.string().trim().min(1, ApiError.CITY_REQUIRED),
  country: z13.string().trim().length(2, ApiError.COUNTRY_REQUIRED),
  // Geocoordinates from the onboarding map place-picker (optional + nullable —
  // see clubInfoUpdateSchema in club.ts).
  lat: z13.number().min(-90, ApiError.VALIDATION_FAILED).max(90, ApiError.VALIDATION_FAILED).nullable().optional(),
  lng: z13.number().min(-180, ApiError.VALIDATION_FAILED).max(180, ApiError.VALIDATION_FAILED).nullable().optional(),
  phone: z13.string().trim().min(1, ApiError.PHONE_INVALID),
  website: z13.string().trim().url(ApiError.WEBSITE_INVALID).optional().or(z13.literal("").transform(() => void 0)),
  workingHoursStart: z13.string().regex(HHMM, ApiError.WORKING_HOURS_INVALID),
  workingHoursEnd: z13.string().regex(HHMM, ApiError.WORKING_HOURS_INVALID),
  bookingDaysAdvance: z13.number().int(ApiError.BOOKING_DAYS_INVALID).min(1, ApiError.BOOKING_DAYS_INVALID).max(90, ApiError.BOOKING_DAYS_INVALID),
  sports: z13.array(
    z13.object({
      name: z13.enum(SPORT_KEYS, { message: ApiError.SPORT_INVALID }),
      sessionDurationMinutes: z13.number().int(ApiError.SESSION_DURATION_INVALID).min(15, ApiError.SESSION_DURATION_INVALID).max(240, ApiError.SESSION_DURATION_INVALID)
    })
  ).min(1, ApiError.SPORTS_REQUIRED)
});
var courtsSchema = z13.object({
  courts: z13.array(
    z13.object({
      sportId: z13.string().min(1),
      name: z13.string().trim().min(1, ApiError.COURT_NAME_REQUIRED),
      isIndoor: z13.boolean(),
      surface: z13.enum(SURFACE_KEYS, { message: ApiError.COURT_SURFACE_INVALID }).nullable().optional(),
      pricePerSession: z13.number().min(0, ApiError.COURT_PRICE_INVALID).max(1e6, ApiError.COURT_PRICE_INVALID),
      workingHoursStart: z13.string().regex(HHMM, ApiError.WORKING_HOURS_INVALID).nullable().optional(),
      workingHoursEnd: z13.string().regex(HHMM, ApiError.WORKING_HOURS_INVALID).nullable().optional()
    })
  ).min(1, ApiError.COURTS_REQUIRED)
});

// src/public-bookings.ts
import { z as z14 } from "zod";
var rescheduleBodySchema = z14.object({
  courtId: z14.string().min(1),
  date: z14.string().regex(ISO_DATE),
  startTime: z14.string().regex(/^\d{2}:\d{2}$/)
});

// src/membership.ts
import { z as z15 } from "zod";
var membershipListQuery = z15.object({
  status: z15.enum(["PENDING", "APPROVED", "REJECTED"]).optional(),
  query: z15.string().trim().max(200).optional(),
  page: z15.coerce.number().int().min(1).optional(),
  pageSize: z15.coerce.number().int().min(1).max(100).optional()
});
var memberSearchQuery = z15.object({
  q: z15.string().trim().optional().catch(void 0)
});
var membershipIdParams = z15.object({ membershipId: idParam });
var membershipBanBody = z15.object({
  duration: z15.enum(["7d", "30d", "90d", "never"])
});

// src/admin-management.ts
import { z as z16 } from "zod";
var adminUserParams = z16.object({
  clubId: idParam,
  userId: idParam
});
var adminUpdateProfileSchema = z16.object({
  firstName: z16.string().trim().min(2, ApiError.FIRSTNAME_TOO_SHORT).optional(),
  lastName: z16.string().trim().min(2, ApiError.LASTNAME_TOO_SHORT).optional(),
  phone: z16.string().trim().optional(),
  email: z16.string().trim().email(ApiError.EMAIL_INVALID).optional()
}).strict();

// src/booking.ts
import { z as z17 } from "zod";
var rosterPlayerSchema = z17.object({
  name: z17.string(),
  playerUserId: z17.string().nullable().optional()
});
var publicBookingSchema = z17.object({
  courtId: z17.string().min(1),
  sportId: z17.string().min(1),
  date: z17.string().regex(ISO_DATE),
  startTime: z17.string().regex(HHMM),
  playerName: z17.string().trim().nullable().optional(),
  playerPhone: z17.string().trim().nullable().optional(),
  playerEmail: z17.string().trim().nullable().optional(),
  players: z17.array(rosterPlayerSchema).optional(),
  playerNames: z17.array(z17.string()).optional(),
  // Optional free-text note from the player. Only persisted when the club has
  // `collectBookingComment` on — silently dropped otherwise so a sneaky client
  // can't bypass the toggle. Bounded so we don't store unbounded blobs.
  comment: z17.string().trim().max(500).nullable().optional(),
  language: z17.enum(["sr", "en", "ru"]).optional().catch(void 0),
  // Coached session: the chosen coach. The handler validates coaching is
  // enabled, the coach teaches the sport, is available, and isn't already
  // booked, and auto-assigns a free court when courtId is omitted.
  coachId: z17.string().min(1).nullable().optional(),
  // Optional equipment-rental lines. Only honored when the club has
  // `allowGearRental` on; the handler validates each item belongs to the club +
  // sport, is active, and has enough free stock for the chosen slot, then
  // snapshots name + unit price onto BookingGear. Silently ignored when off.
  gear: z17.array(
    z17.object({
      gearItemId: z17.string().min(1),
      quantity: z17.number().int().min(1).max(20)
    })
  ).max(20).optional()
});
var availabilityQuery = z17.object({
  startDate: z17.string().regex(ISO_DATE).optional().catch(void 0),
  days: z17.coerce.number().int().min(1).optional().catch(void 0)
});
var scheduleQuery = z17.object({
  date: z17.string().regex(ISO_DATE).optional().catch(void 0)
});
var bookingParams = z17.object({
  clubId: idParam,
  bookingId: idParam
});
var bookingSeriesParams = z17.object({
  clubId: idParam,
  seriesId: idParam
});
var adminCreateBookingSchema = z17.object({
  courtId: z17.string().min(1),
  sportId: z17.string().min(1),
  date: z17.string().regex(ISO_DATE),
  startTime: z17.string().regex(HHMM),
  playerName: z17.string().trim().min(2),
  playerPhone: z17.string().trim().optional().nullable(),
  playerEmail: z17.string().trim().optional().nullable(),
  comment: z17.string().optional().nullable(),
  players: z17.array(rosterPlayerSchema).optional(),
  playerNames: z17.array(z17.string()).optional(),
  // When true, skips quota enforcement (gated by `override_booking_limits`).
  override: z17.boolean().optional(),
  // Coached session booked by the front desk for a calling player. The handler
  // validates the coach teaches the sport, is available, and isn't double-booked.
  coachId: z17.string().min(1).nullable().optional()
});
var noShowBodySchema = z17.object({
  noShow: z17.boolean()
});
var adminRescheduleBodySchema = z17.object({
  courtId: z17.string().min(1),
  date: z17.string().regex(ISO_DATE),
  startTime: z17.string().regex(/^\d{2}:\d{2}$/)
});
var bulkBookingSchema = z17.object({
  courtId: z17.string().min(1),
  sportId: z17.string().min(1),
  startTime: z17.string().regex(HHMM),
  playerName: z17.string().trim().min(2),
  playerPhone: z17.string().trim().optional().nullable(),
  playerEmail: z17.string().trim().optional().nullable(),
  comment: z17.string().optional().nullable(),
  recurrence: recurrenceSchema
});
var adminBookingsListQuery = z17.object({
  allTime: z17.string().optional().catch(void 0),
  startDate: z17.string().optional().catch(void 0),
  endDate: z17.string().optional().catch(void 0),
  includeCancelled: z17.string().optional().catch(void 0),
  noShow: z17.string().optional().catch(void 0),
  courtId: z17.string().optional().catch(void 0),
  playerName: z17.string().optional().catch(void 0),
  playerPhone: z17.string().optional().catch(void 0),
  page: z17.string().optional().catch(void 0),
  pageSize: z17.string().optional().catch(void 0)
});
var seriesCancelSchema = z17.object({
  fromDate: z17.string().regex(ISO_DATE).optional().catch(void 0)
});

// src/club.ts
import { z as z18 } from "zod";
var CLUB_SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
var clubDeleteSchema = z18.object({
  confirm: z18.string().min(1, ApiError.DELETE_CONFIRM_MISMATCH)
});
var clubIdentityUpdateSchema = z18.object({
  name: z18.string().trim().min(2, ApiError.CLUBNAME_TOO_SHORT),
  slug: z18.string().trim().min(2, ApiError.CLUBSLUG_TOO_SHORT).regex(CLUB_SLUG_RE, ApiError.CLUBSLUG_INVALID)
});
var shareClubLinkSchema = z18.object({
  email: z18.string().trim().email(ApiError.EMAIL_INVALID).max(200),
  language: z18.enum(["sr", "en", "ru"]).optional()
});
var clubFeaturesSchema = z18.object({
  collectPhone: z18.boolean(),
  requirePhone: z18.boolean(),
  requirePhoneOtp: z18.boolean(),
  sendBookingConfirmationSms: z18.boolean(),
  sendBookingReminderSms: z18.boolean(),
  collectEmail: z18.boolean(),
  requireEmail: z18.boolean(),
  requireEmailOtp: z18.boolean(),
  sendBookingConfirmationEmail: z18.boolean(),
  sendBookingReminderEmail: z18.boolean(),
  requireLogin: z18.boolean(),
  requireMembership: z18.boolean(),
  // Optional: these two are owned by the Time Slots page (where the slots are
  // configured). The manager Features form omits them; the superadmin club
  // dialog still sends them. Omitted → Prisma skips the column (no overwrite).
  allowMemberOnlySlots: z18.boolean().optional(),
  allowPrimeTimeSlots: z18.boolean().optional(),
  showPublicSchedule: z18.boolean(),
  collectBookingComment: z18.boolean(),
  // Coached sessions. Owned by the manager Features form; optional so the
  // superadmin club dialog (which omits it) leaves the column untouched.
  // Tier-gated (PRO) server-side. See packages/schemas/coaching.ts.
  allowCoaching: z18.boolean().optional(),
  // Match results / leaderboard. Optional so the superadmin club dialog leaves it
  // untouched when omitted. Not tier-gated.
  resultsEnabled: z18.boolean().optional(),
  // Competitions / leagues. Optional so the superadmin club dialog leaves it
  // untouched when omitted; managed primarily from the Competitions page (see
  // clubCompetitionsSchema). Tier-gated (PRO) server-side.
  competitionsEnabled: z18.boolean().optional()
});
var clubCompetitionsSchema = z18.object({
  competitionsEnabled: z18.boolean()
});
var clubCoachingSchema = z18.object({
  allowCoaching: z18.boolean()
});
var clubFeedbackSchema = z18.object({
  feedbackEnabled: z18.boolean(),
  feedbackViaEmail: z18.boolean(),
  feedbackViaWeb: z18.boolean()
});
var clubLimitsSchema = z18.object({
  limitBookingsPerContactPerDay: z18.boolean(),
  maxBookingsPerContactPerDay: z18.number().int(ApiError.BOOKING_LIMIT_INVALID).min(1, ApiError.BOOKING_LIMIT_INVALID).max(100, ApiError.BOOKING_LIMIT_INVALID),
  maxBookingsPerCreationDay: z18.number().int(ApiError.BOOKING_LIMIT_INVALID).min(1, ApiError.BOOKING_LIMIT_INVALID).max(100, ApiError.BOOKING_LIMIT_INVALID).nullable(),
  enforceBookingQuotas: z18.boolean(),
  maxBookingsPerMemberPerDay: z18.number().int(ApiError.BOOKING_LIMIT_INVALID).min(1, ApiError.BOOKING_LIMIT_INVALID).max(100, ApiError.BOOKING_LIMIT_INVALID).nullable(),
  preventConsecutiveDayBooking: z18.boolean(),
  lateBookingExemptionHours: z18.number().int(ApiError.BOOKING_LIMIT_INVALID).min(1, ApiError.BOOKING_LIMIT_INVALID).max(72, ApiError.BOOKING_LIMIT_INVALID).nullable(),
  maxPrimeBookingsPerWeek: z18.number().int(ApiError.BOOKING_LIMIT_INVALID).min(1, ApiError.BOOKING_LIMIT_INVALID).max(100, ApiError.BOOKING_LIMIT_INVALID).nullable(),
  requirePlayerRoster: z18.boolean()
}).strict();
var CLUB_AMENITIES = [
  "PARKING",
  "SHOWERS",
  "CAFE_BAR",
  "FOOD",
  "GEAR_RENTAL",
  "WIFI",
  "CHANGING_ROOMS",
  "LOCKERS",
  "PRO_SHOP",
  "TOILETS",
  "AIR_CONDITIONING",
  "SPECTATOR_SEATING",
  "WHEELCHAIR_ACCESS"
];
var clubAmenitiesSchema = z18.object({
  amenities: z18.array(z18.enum(CLUB_AMENITIES, { message: ApiError.AMENITY_INVALID })).max(CLUB_AMENITIES.length)
});
var longHtmlNullable = z18.string().trim().max(2e4).nullable().optional().or(z18.literal("").transform(() => null));
var clubRulesSchema = z18.object({
  rules: longHtmlNullable,
  rulesSr: longHtmlNullable,
  rulesEn: longHtmlNullable,
  rulesRu: longHtmlNullable
});
var subscriptionSchema = z18.object({
  // YYYY-MM-DD or null. Empty string also clears.
  subscribedUntil: z18.union([z18.string().regex(ISO_DATE), z18.literal(""), z18.null()]).nullable(),
  tier: z18.enum(["BASIC", "PRO", "ENTERPRISE"]),
  maxCourts: z18.number().int(ApiError.MAX_COURTS_INVALID).min(1, ApiError.MAX_COURTS_INVALID).max(1e3, ApiError.MAX_COURTS_INVALID).nullable().optional()
});
var optionalTrimmedNullable = z18.string().trim().max(2e3).nullable().optional().or(z18.literal("").transform(() => null));
var clubInfoUpdateSchema = z18.object({
  address: z18.string().trim().min(3, ApiError.ADDRESS_REQUIRED),
  city: z18.string().trim().min(1, ApiError.CITY_REQUIRED),
  country: z18.string().trim().length(2, ApiError.COUNTRY_REQUIRED),
  // Geocoordinates from the admin map place-picker. Optional + nullable: a club
  // whose address Google can't pin still saves, and the public page falls back
  // to an address-text link. Bounds are belt-and-suspenders (values come from
  // Google, never typed), so the generic code is fine.
  lat: z18.number().min(-90, ApiError.VALIDATION_FAILED).max(90, ApiError.VALIDATION_FAILED).nullable().optional(),
  lng: z18.number().min(-180, ApiError.VALIDATION_FAILED).max(180, ApiError.VALIDATION_FAILED).nullable().optional(),
  phone: z18.string().trim().min(1, ApiError.PHONE_INVALID),
  website: z18.string().trim().url(ApiError.WEBSITE_INVALID).nullable().optional().or(z18.literal("").transform(() => null)),
  workingHoursStart: z18.string().regex(HHMM, ApiError.WORKING_HOURS_INVALID),
  workingHoursEnd: z18.string().regex(HHMM, ApiError.WORKING_HOURS_INVALID),
  bookingDaysAdvance: z18.number().int(ApiError.BOOKING_DAYS_INVALID).min(1, ApiError.BOOKING_DAYS_INVALID).max(90, ApiError.BOOKING_DAYS_INVALID),
  isFree: z18.boolean(),
  // Optional translations and subheading shown below the club title on the
  // public booking page (and used in <meta description> for SEO).
  nameEn: optionalTrimmedNullable,
  nameRu: optionalTrimmedNullable,
  descriptionSr: optionalTrimmedNullable,
  descriptionEn: optionalTrimmedNullable,
  descriptionRu: optionalTrimmedNullable
});
var sportsUpdateSchema = z18.object({
  sports: z18.array(
    z18.object({
      id: z18.string().optional(),
      name: z18.enum(SPORT_KEYS, { message: ApiError.SPORT_INVALID }),
      sessionDurationMinutes: z18.number().int(ApiError.SESSION_DURATION_INVALID).min(15, ApiError.SESSION_DURATION_INVALID).max(240, ApiError.SESSION_DURATION_INVALID)
    })
  ).min(1, ApiError.SPORTS_REQUIRED)
});
var adminClubsQuery = z18.object({
  q: z18.string().optional().catch(void 0),
  sport: z18.string().optional().catch(void 0),
  page: z18.string().optional().catch(void 0),
  pageSize: z18.string().optional().catch(void 0),
  limit: z18.string().optional().catch(void 0)
});
var editImpactQuery = z18.object({
  kind: z18.enum([
    "sport-duration",
    "court-sport",
    "court-hide",
    "court-unsplit",
    "sport-rename",
    "club-hours",
    "court-hours"
  ]),
  sportId: z18.string().optional(),
  courtId: z18.string().optional(),
  sessionDurationMinutes: z18.string().optional(),
  name: z18.string().optional(),
  workingHoursStart: z18.string().regex(HHMM).optional(),
  workingHoursEnd: z18.string().regex(HHMM).optional()
});

// src/court.ts
import { z as z19 } from "zod";
var courtBaseSchema = z19.object({
  sportId: z19.string().min(1),
  name: z19.string().trim().min(1, ApiError.COURT_NAME_REQUIRED),
  // Optional translations of the court name. Stored as-is, surfaced on the
  // public booking page when the visitor's locale matches.
  nameEn: z19.string().trim().max(200).nullable().optional().or(z19.literal("").transform(() => null)),
  nameRu: z19.string().trim().max(200).nullable().optional().or(z19.literal("").transform(() => null)),
  isIndoor: z19.boolean(),
  surface: z19.enum(SURFACE_KEYS, { message: ApiError.COURT_SURFACE_INVALID }).nullable().optional(),
  // Football court format (5-a-side, 7-a-side, …). Sport-scoped like `surface`;
  // the route handler rejects a value that isn't valid for the court's sport.
  size: z19.enum(SIZE_KEYS, { message: ApiError.COURT_SIZE_INVALID }).nullable().optional(),
  pricePerSession: z19.number().min(0, ApiError.COURT_PRICE_INVALID).max(1e6, ApiError.COURT_PRICE_INVALID),
  workingHoursStart: z19.string().regex(HHMM, ApiError.WORKING_HOURS_INVALID).nullable().optional(),
  workingHoursEnd: z19.string().regex(HHMM, ApiError.WORKING_HOURS_INVALID).nullable().optional(),
  isActive: z19.boolean().optional(),
  // Split config — only meaningful for BASKETBALL (the route enforces the
  // sport). When `isSplittable` is true the whole court owns two
  // independently-bookable halves, each priced at `halfPrice`. The optional
  // half names default to localized Left/Right in the admin UI. Omitted (or
  // false) leaves the court whole — the legacy behavior.
  isSplittable: z19.boolean().optional(),
  halfPrice: z19.number().min(0, ApiError.COURT_PRICE_INVALID).max(1e6, ApiError.COURT_PRICE_INVALID).nullable().optional(),
  halfNameLeft: z19.string().trim().max(200).nullable().optional().or(z19.literal("").transform(() => null)),
  halfNameRight: z19.string().trim().max(200).nullable().optional().or(z19.literal("").transform(() => null))
});
var courtsOrderSchema = z19.object({
  courtIds: z19.array(z19.string().min(1))
});
var courtParams = z19.object({ clubId: idParam, courtId: idParam });

// src/tier.ts
var TIER_RANK = {
  BASIC: 0,
  PRO: 1,
  ENTERPRISE: 2
};
var GATED_BOOLEAN_FEATURES = [
  "requireLogin",
  "requireMembership",
  "allowMemberOnlySlots",
  "allowPrimeTimeSlots",
  "requireEmailOtp",
  "requirePhoneOtp",
  "sendBookingConfirmationSms",
  "sendBookingReminderEmail",
  "sendBookingReminderSms",
  "limitBookingsPerContactPerDay",
  "enforceBookingQuotas",
  "preventConsecutiveDayBooking",
  "showPublicSchedule",
  "requirePlayerRoster",
  "allowCoaching",
  "competitionsEnabled"
];
var GATED_NULLABLE_FEATURES = [
  "maxPrimeBookingsPerWeek",
  "maxBookingsPerMemberPerDay",
  "lateBookingExemptionHours",
  "maxBookingsPerCreationDay",
  "maxBookingsPerContactPerDay"
];
var FEATURE_MIN_TIER = {
  requireLogin: "PRO",
  requireMembership: "PRO",
  allowMemberOnlySlots: "PRO",
  allowPrimeTimeSlots: "PRO",
  requireEmailOtp: "ENTERPRISE",
  requirePhoneOtp: "ENTERPRISE",
  sendBookingConfirmationSms: "ENTERPRISE",
  sendBookingReminderEmail: "PRO",
  sendBookingReminderSms: "ENTERPRISE",
  limitBookingsPerContactPerDay: "PRO",
  enforceBookingQuotas: "PRO",
  preventConsecutiveDayBooking: "PRO",
  showPublicSchedule: "PRO",
  requirePlayerRoster: "PRO",
  allowCoaching: "PRO",
  competitionsEnabled: "PRO",
  maxPrimeBookingsPerWeek: "PRO",
  maxBookingsPerMemberPerDay: "PRO",
  lateBookingExemptionHours: "PRO",
  maxBookingsPerCreationDay: "PRO",
  maxBookingsPerContactPerDay: "PRO"
};
function hasFeature(tier, feature) {
  return TIER_RANK[tier] >= TIER_RANK[FEATURE_MIN_TIER[feature]];
}
function isNonDefaultFeatureValue(feature, value) {
  if (GATED_BOOLEAN_FEATURES.includes(feature))
    return value === true;
  if (feature === "maxBookingsPerContactPerDay") return false;
  return value !== null && value !== void 0;
}
function effectiveClubFlags(club) {
  const tier = club.subscriptionTier;
  const masked = { ...club };
  for (const feature of GATED_BOOLEAN_FEATURES) {
    if (!hasFeature(tier, feature) && masked[feature] !== void 0) {
      masked[feature] = false;
    }
  }
  for (const feature of GATED_NULLABLE_FEATURES) {
    if (!hasFeature(tier, feature) && masked[feature] !== void 0) {
      masked[feature] = null;
    }
  }
  return masked;
}

// src/coaching.ts
import { z as z20 } from "zod";
var coachParams = z20.object({ clubId: idParam, coachId: idParam });
var coachProfileUpdateSchema = z20.object({
  bio: z20.string().trim().max(1e3).nullable().optional().or(z20.literal("").transform(() => null)),
  isActive: z20.boolean(),
  displayOrder: z20.number().int().min(0).max(1e4).nullable().optional()
});
var coachSportsSchema = z20.object({
  sports: z20.array(
    z20.object({
      sportId: z20.string().min(1),
      pricePerSession: z20.number().min(0, ApiError.COACH_PRICE_INVALID).max(1e6, ApiError.COACH_PRICE_INVALID)
    })
  ).max(20)
});
var coachAvailabilitySchema = z20.object({
  windows: z20.array(
    z20.object({
      dayOfWeek: z20.number().int().min(0).max(6),
      startTime: z20.string().regex(HHMM, ApiError.COACH_AVAILABILITY_INVALID),
      endTime: z20.string().regex(HHMM, ApiError.COACH_AVAILABILITY_INVALID)
    }).refine((w) => w.startTime < w.endTime, {
      message: ApiError.COACH_AVAILABILITY_INVALID,
      path: ["endTime"]
    })
  ).max(100)
});
var coachTimeOffSchema = z20.object({
  entries: z20.array(
    z20.object({
      date: z20.string().regex(ISO_DATE),
      startTime: z20.string().regex(HHMM).nullable().optional(),
      endTime: z20.string().regex(HHMM).nullable().optional(),
      reason: z20.string().trim().max(200).nullable().optional()
    }).refine(
      (e) => e.startTime == null && e.endTime == null || e.startTime != null && e.endTime != null && e.startTime < e.endTime,
      { message: ApiError.COACH_TIME_OFF_INVALID, path: ["endTime"] }
    )
  ).max(365)
});
var coachingAvailabilityQuery = z20.object({
  coachId: z20.string().min(1),
  sportId: z20.string().min(1),
  days: z20.coerce.number().int().min(1).optional().catch(void 0)
});
var coachesForSlotQuery = z20.object({
  courtId: z20.string().min(1),
  date: z20.string().regex(ISO_DATE),
  startTime: z20.string().regex(HHMM)
});

// src/gear.ts
import { z as z21 } from "zod";
var gearParams = z21.object({ clubId: idParam, gearItemId: idParam });
var gearItemCreateSchema = z21.object({
  sportId: z21.string().min(1, ApiError.GEAR_SPORT_INVALID),
  name: z21.string().trim().min(1, ApiError.GEAR_NAME_REQUIRED).max(100, ApiError.GEAR_NAME_REQUIRED),
  description: z21.string().trim().max(500).nullable().optional().or(z21.literal("").transform(() => null)),
  pricePerSession: z21.number().min(0, ApiError.GEAR_PRICE_INVALID).max(1e6, ApiError.GEAR_PRICE_INVALID),
  quantity: z21.number().int(ApiError.GEAR_QUANTITY_INVALID).min(1, ApiError.GEAR_QUANTITY_INVALID).max(1e3, ApiError.GEAR_QUANTITY_INVALID),
  isActive: z21.boolean(),
  displayOrder: z21.number().int().min(0).max(1e4).nullable().optional()
});
var gearItemUpdateSchema = gearItemCreateSchema;
var gearSettingsSchema = z21.object({ allowGearRental: z21.boolean() });
var gearForSlotQuery = z21.object({
  courtId: z21.string().min(1),
  date: z21.string().regex(ISO_DATE),
  startTime: z21.string().regex(HHMM)
});
function sumGearTotal(lines) {
  const total = lines.reduce(
    (acc, l) => acc + Number(l.unitPrice) * l.quantity,
    0
  );
  return (Math.round(total * 100) / 100).toString();
}

// src/review.ts
import { z as z22 } from "zod";
var submitReviewSchema = z22.object({
  rating: z22.number().int().min(1).max(5),
  comment: z22.string().trim().max(1e3).optional(),
  bookingId: z22.string().optional(),
  source: z22.enum(["WEB", "EMAIL"]).default("WEB")
});
var submitReviewByTokenSchema = z22.object({
  rating: z22.number().int().min(1).max(5),
  comment: z22.string().trim().max(1e3).optional()
});

// src/matches.ts
import { z as z23 } from "zod";
var matchSetSchema = z23.object({
  a: z23.number().int().min(0).max(99),
  b: z23.number().int().min(0).max(99),
  tb: z23.number().int().min(0).max(99).optional()
});
var matchParticipantInputSchema = z23.object({
  name: z23.string().trim().min(1, ApiError.MATCH_PLAYER_NAME_REQUIRED).max(100, ApiError.MATCH_PLAYER_NAME_REQUIRED),
  playerUserId: z23.string().trim().max(128).nullish().transform((v) => v && v.length > 0 ? v : null),
  side: z23.union([z23.literal(0), z23.literal(1)]),
  position: z23.number().int().min(0).max(20).optional()
});
var recordMatchSchema = z23.object({
  bookingId: idParam,
  // Everyone except the booker, with their side. Empty for a singles match at a
  // club that didn't pre-fill the opponent — the server then has nobody on
  // side 1, which is rejected with MATCH_RESULT_INVALID.
  participants: z23.array(matchParticipantInputSchema).max(12),
  // Racket sports: per-set games. Omitted for sports recorded as a plain
  // win/loss (then `winnerSide` and/or `scoreSummary` carry the result).
  sets: z23.array(matchSetSchema).max(7).optional(),
  // Explicit winner. Omitted + sets present → derived from the sets. Null = draw
  // (or "no winner" for sports where that applies).
  winnerSide: z23.union([z23.literal(0), z23.literal(1)]).nullish(),
  // Free-text score for non-racket sports (e.g. "3-2"). Ignored when `sets` is
  // present — the server builds the summary from the sets in that case.
  scoreSummary: z23.string().trim().max(40).optional(),
  notes: z23.string().trim().max(2e3).optional()
});
var dismissResultSchema = z23.object({
  bookingId: idParam
});
var disputeMatchSchema = z23.object({
  reason: z23.string().trim().max(500).optional()
});
var matchIdParams = z23.object({ matchId: idParam });
var matchHistoryQuery = z23.object({
  sport: z23.string().trim().max(40).optional().catch(void 0),
  clubId: z23.string().trim().max(128).optional().catch(void 0),
  result: z23.enum(["WON", "LOST"]).optional().catch(void 0),
  format: z23.enum(["SINGLES", "DOUBLES", "OTHER"]).optional().catch(void 0),
  // Head-to-head drill-down: only matches that also include this opponent.
  opponentId: z23.string().trim().max(128).optional().catch(void 0),
  page: z23.coerce.number().int().min(1).catch(1).default(1),
  pageSize: z23.coerce.number().int().min(1).max(50).catch(20).default(20)
});
var playerSearchQuery = z23.object({
  q: z23.string().trim().optional().catch(void 0)
});

// src/competitions.ts
import { z as z24 } from "zod";
var competitionFormatEnum = z24.enum(
  ["LEAGUE", "GROUPS_KNOCKOUT", "KNOCKOUT"],
  { message: ApiError.COMPETITION_FORMAT_INVALID }
);
var competitionStatusEnum = z24.enum([
  "DRAFT",
  "REGISTRATION_OPEN",
  "REGISTRATION_CLOSED",
  "IN_PROGRESS",
  "COMPLETED",
  "CANCELLED"
]);
var competitionParticipantStatusEnum = z24.enum([
  "PENDING",
  "CONFIRMED",
  "WITHDRAWN",
  "REJECTED"
]);
var nameField = z24.string().trim().min(2, ApiError.COMPETITION_NAME_REQUIRED).max(120, ApiError.COMPETITION_NAME_REQUIRED);
var nullableText = (max) => z24.string().trim().max(max).nullish().transform((v) => v && v.length > 0 ? v : null);
var nullableDate = z24.union([dateString, z24.literal("")]).nullish().transform((v) => v && v.length > 0 ? v : null);
var nullableTime = z24.union([z24.string().regex(HHMM), z24.literal("")]).nullish().transform((v) => v && v.length > 0 ? v : null);
var pointsField = z24.number().int().min(0).max(100);
var divisionsField = z24.array(z24.string().trim().min(1).max(24)).max(32).optional();
var competitionIdParams = z24.object({ competitionId: idParam });
var competitionParticipantParams = z24.object({
  competitionId: idParam,
  participantId: idParam
});
var competitionFixtureParams = z24.object({
  competitionId: idParam,
  matchId: idParam
});
var clubCompetitionParams = z24.object({
  slug: slugParam,
  competitionId: idParam
});
var fixtureMatchParams = z24.object({ matchId: idParam });
var createCompetitionSchema = z24.object({
  sportId: idParam,
  name: nameField,
  format: competitionFormatEnum,
  season: nullableText(60),
  description: nullableText(5e3),
  registrationOpensDate: nullableDate,
  registrationClosesDate: nullableDate,
  startDate: nullableDate,
  endDate: nullableDate,
  pointsWin: pointsField.default(2),
  pointsDraw: pointsField.default(1),
  pointsLoss: pointsField.default(0),
  bestOfSets: z24.number().int().min(1).max(9).nullish().default(null),
  thirdPlaceMatch: z24.boolean().default(false),
  divisions: divisionsField,
  maxParticipants: z24.number().int().min(2).max(1024).nullish().default(null)
});
var updateCompetitionSchema = z24.object({
  name: nameField.optional(),
  format: competitionFormatEnum.optional(),
  season: nullableText(60),
  description: nullableText(5e3),
  registrationOpensDate: nullableDate,
  registrationClosesDate: nullableDate,
  startDate: nullableDate,
  endDate: nullableDate,
  pointsWin: pointsField.optional(),
  pointsDraw: pointsField.optional(),
  pointsLoss: pointsField.optional(),
  bestOfSets: z24.number().int().min(1).max(9).nullish(),
  thirdPlaceMatch: z24.boolean().optional(),
  divisions: divisionsField,
  maxParticipants: z24.number().int().min(2).max(1024).nullish()
});
var competitionStatusUpdateSchema = z24.object({
  status: competitionStatusEnum
});
var addParticipantSchema = z24.object({
  name: z24.string().trim().min(1).max(100),
  playerUserId: idParam.nullish(),
  phone: nullableText(40),
  email: nullableText(200),
  partnerName: nullableText(100),
  partnerPlayerUserId: idParam.nullish(),
  divisionLabel: nullableText(24),
  groupName: nullableText(24),
  seed: z24.number().int().min(1).max(1024).nullish(),
  // Manager-added entrants are confirmed by default.
  status: competitionParticipantStatusEnum.default("CONFIRMED")
});
var updateParticipantSchema = z24.object({
  name: z24.string().trim().min(1).max(100).optional(),
  divisionLabel: nullableText(24),
  groupName: nullableText(24),
  seed: z24.number().int().min(1).max(1024).nullish(),
  note: nullableText(500)
});
var decideParticipantSchema = z24.object({
  status: competitionParticipantStatusEnum
});
var bulkAssignSchema = z24.object({
  assignments: z24.array(
    z24.object({
      participantId: idParam,
      divisionLabel: nullableText(24),
      groupName: nullableText(24),
      seed: z24.number().int().min(1).max(1024).nullish()
    })
  ).min(1).max(512)
});
var autoAssignGroupsSchema = z24.object({
  divisionLabel: nullableText(24),
  groupCount: z24.number().int().min(1).max(16)
});
var generateGroupFixturesSchema = z24.object({
  divisionLabel: nullableText(24),
  doubleRound: z24.boolean().default(false)
});
var generateBracketSchema = z24.object({
  divisionLabel: nullableText(24),
  source: z24.enum(["MANUAL", "GROUPS"]),
  seeds: z24.array(idParam).max(256).optional(),
  advancersPerGroup: z24.number().int().min(1).max(8).optional(),
  thirdPlace: z24.boolean().optional()
}).refine(
  (v) => v.source === "GROUPS" ? true : !!v.seeds && v.seeds.length >= 2,
  { message: ApiError.COMPETITION_INVALID_STATE, path: ["seeds"] }
);
var fixtureResultSchema = z24.object({
  sets: z24.array(matchSetSchema).max(7).optional(),
  winnerSide: z24.union([z24.literal(0), z24.literal(1)]).nullish(),
  scoreSummary: z24.string().trim().max(40).optional(),
  notes: z24.string().trim().max(2e3).optional()
});
var rescheduleFixtureSchema = z24.object({
  scheduledDate: nullableDate,
  scheduledTime: nullableTime
});
var registerCompetitionSchema = z24.object({
  note: nullableText(500),
  phone: nullableText(40),
  email: nullableText(200),
  partnerName: nullableText(100),
  partnerPlayerUserId: idParam.nullish()
});
export {
  ApiError,
  CLUB_AMENITIES,
  FEATURE_MIN_TIER,
  HANDLE_RE,
  HHMM,
  ISO_DATE,
  SIZE_KEYS,
  SPORT_KEYS,
  SPORT_SIZES,
  SPORT_SURFACES,
  STAFF_PERMISSION_DEFAULTS,
  SURFACE_KEYS,
  TIER_RANK,
  addParticipantSchema,
  addPrice,
  adminBookingsListQuery,
  adminClubsQuery,
  adminCreateBookingSchema,
  adminRescheduleBodySchema,
  adminUpdateProfileSchema,
  adminUserParams,
  autoAssignGroupsSchema,
  availabilityQuery,
  blockBulkSchema,
  blockIdParams,
  blockSeriesParams,
  blocksListQuery,
  bookingParams,
  bookingSeriesParams,
  bulkAssignSchema,
  bulkBookingSchema,
  changePasswordSchema,
  closureBulkSchema,
  closureIdParams,
  closureSeriesParams,
  clubAmenitiesSchema,
  clubCoachingSchema,
  clubCompetitionParams,
  clubCompetitionsSchema,
  clubDeleteSchema,
  clubFeaturesSchema,
  clubFeedbackSchema,
  clubIdParams,
  clubIdentityUpdateSchema,
  clubInfoSchema,
  clubInfoUpdateSchema,
  clubLimitsSchema,
  clubRulesSchema,
  clubSlugParams,
  coachAvailabilitySchema,
  coachParams,
  coachProfileUpdateSchema,
  coachSportsSchema,
  coachTimeOffSchema,
  coachesForSlotQuery,
  coachingAvailabilityQuery,
  competitionFixtureParams,
  competitionFormatEnum,
  competitionIdParams,
  competitionParticipantParams,
  competitionParticipantStatusEnum,
  competitionStatusEnum,
  competitionStatusUpdateSchema,
  confirmEmailVerificationSchema,
  confirmPhoneVerificationSchema,
  contactSchema,
  courtBaseSchema,
  courtParams,
  courtPriceRulesSchema,
  courtTimeSlotsUpdateSchema,
  courtsOrderSchema,
  courtsSchema,
  createAccountSchema,
  createCompetitionSchema,
  dateRangeQuery,
  dateString,
  dayOfWeekOf,
  decideParticipantSchema,
  dismissResultSchema,
  disputeMatchSchema,
  editImpactQuery,
  effectiveClubFlags,
  fixtureMatchParams,
  fixtureResultSchema,
  forgotPasswordSchema,
  fromDateQuery,
  gearForSlotQuery,
  gearItemCreateSchema,
  gearItemUpdateSchema,
  gearParams,
  gearSettingsSchema,
  generateBracketSchema,
  generateGroupFixturesSchema,
  handleSchema,
  hasFeature,
  hasPermission,
  hhmmString,
  idParam,
  inviteSchema,
  isNonDefaultFeatureValue,
  isSizeValidForSport,
  isSurfaceValidForSport,
  loginSchema,
  matchHistoryQuery,
  matchIdParams,
  matchParticipantInputSchema,
  matchSetSchema,
  memberSearchQuery,
  membershipBanBody,
  membershipIdParams,
  membershipListQuery,
  mergeStaffPermissions,
  noShowBodySchema,
  normalizePhone,
  paginationQuery,
  passwordSchema,
  playerProfileSchema,
  playerSearchQuery,
  priceRuleSchema,
  priceRulesOverlap,
  publicBookingSchema,
  recordMatchSchema,
  recurrenceSchema,
  registerCompetitionSchema,
  registerSchema,
  requestEmailVerificationSchema,
  requestPhoneVerificationSchema,
  rescheduleBodySchema,
  rescheduleFixtureSchema,
  resetPasswordSchema,
  resolveSlotPrice,
  scheduleQuery,
  seriesCancelSchema,
  shareClubLinkSchema,
  sizeEnum,
  slotPeriodSchema,
  slugParam,
  sportEnum,
  sportsUpdateSchema,
  staffPermissionsSchema,
  submitReviewByTokenSchema,
  submitReviewSchema,
  subscriptionSchema,
  sumGearTotal,
  surfaceEnum,
  timeSlotRuleSchema,
  timeSlotsUpdateSchema,
  tokenParam,
  tokenParams,
  updateCompetitionSchema,
  updateMeSchema,
  updateParticipantSchema,
  weekdaySchema
};
